import i18n from '../i18n';
import { pb } from '../lib/pb';
import { errorCode, errorMessage } from '../lib/errors';
import type { FeatureRecord, Status } from '../lib/types';
import type { Geometry } from '../geo/types';
import { PRESETS, defaultProps, keyFor, slugify, typeDef, uniqueKey } from '../featureTypes/registry';
import { currentMap, getState, setState, toast } from './store';

export interface PendingConflict {
  featureId: string;
  patch: Partial<FeatureRecord>;
}

let conflictListener: ((c: PendingConflict | null) => void) | null = null;
export function onConflict(fn: (c: PendingConflict | null) => void) {
  conflictListener = fn;
}

function putFeature(rec: FeatureRecord) {
  setState({ features: { ...getState().features, [rec.id]: rec } });
}

export function takenKeys(): Set<string> {
  return new Set(Object.values(getState().features).map((f) => f.key));
}

/** Creates a feature from a draw-menu preset and selects it. */
export async function createFromPreset(presetId: string, geometry: Geometry): Promise<FeatureRecord | null> {
  const map = currentMap();
  const preset = PRESETS.find((p) => p.id === presetId);
  if (!map || !preset) return null;
  const def = typeDef(preset.type);
  const name = i18n.t(`presets.${preset.id}`, { defaultValue: i18n.t(`types.${preset.type}`) });
  const { properties, designProps } = defaultProps(preset.type);
  for (const [k, v] of Object.entries(preset.props)) {
    const p = def.props.find((x) => x.name === k);
    (p?.design ? designProps : properties)[k] = v;
  }

  // Attach to the selected parent when it fits (quest step → quest, spawn point → spawn area).
  let parent = '';
  const sel = getState().selectedId ? getState().features[getState().selectedId!] : null;
  if (sel && def.parentTypes?.includes(sel.type)) {
    parent = sel.id;
    if (preset.type === 'quest_step') {
      const steps = Object.values(getState().features).filter(
        (f) => f.parent === sel.id && f.type === 'quest_step',
      );
      properties.stepIndex = steps.reduce((m, f) => Math.max(m, Number(f.properties.stepIndex) || 0), 0) + 1;
    }
  }

  const key = uniqueKey(keyFor(preset.type, map.keyPrefix, slugify(name)), takenKeys());
  try {
    const rec = await pb.collection('features').create<FeatureRecord>({
      map: map.id,
      type: preset.type,
      key,
      name,
      geometry,
      properties,
      designProps,
      status: 'idea',
      showInGame: def.showInGameDefault,
      parent,
      tags: [],
    });
    putFeature(rec);
    setState({ selectedId: rec.id });
    return rec;
  } catch (err) {
    toast(errorMessage(err), 'error');
    return null;
  }
}

/**
 * PATCH with optimistic concurrency. `baseRev` is the revision the edit was based on (defaults to the
 * latest known). A 409 raises the conflict banner instead of losing either person's edit.
 */
export async function updateFeature(
  id: string,
  patch: Partial<FeatureRecord> & { restoreOf?: string },
  baseRev?: number,
): Promise<FeatureRecord | null> {
  const f = getState().features[id];
  if (!f) return null;
  try {
    const rec = await pb
      .collection('features')
      .update<FeatureRecord>(id, { ...patch, rev: baseRev ?? f.rev });
    putFeature(rec);
    return rec;
  } catch (err) {
    if (errorCode(err) === 'stale_revision') {
      conflictListener?.({ featureId: id, patch });
    } else {
      toast(errorMessage(err), 'error');
    }
    return null;
  }
}

/** Conflict banner: "Reload theirs". */
export async function reloadFeature(id: string) {
  const rec = await pb.collection('features').getOne<FeatureRecord>(id);
  putFeature(rec);
  conflictListener?.(null);
}

/** Conflict banner: "Apply mine again" — re-sends my patch on top of the latest revision. */
export async function reapplyPatch(c: PendingConflict) {
  const latest = await pb.collection('features').getOne<FeatureRecord>(c.featureId);
  putFeature(latest);
  conflictListener?.(null);
  await updateFeature(c.featureId, c.patch);
}

export async function setStatus(id: string, status: Status) {
  const rec = await updateFeature(id, { status });
  if (rec && rec.status !== status)
    toast(i18n.t('inspector.statusAuto', { status: i18n.t(`status.${rec.status}`) }));
}

export async function deleteFeature(id: string) {
  try {
    await pb.collection('features').delete(id);
    const features = { ...getState().features };
    delete features[id];
    setState({ features, selectedId: getState().selectedId === id ? null : getState().selectedId });
  } catch (err) {
    toast(errorMessage(err), 'error');
  }
}

export async function resolveSync(id: string, action: 'take_unity' | 'keep_design' | 'unlink') {
  try {
    await pb.send(`/api/mapeditor/features/${id}/resolve`, { method: 'POST', body: { action } });
    const rec = await pb.collection('features').getOne<FeatureRecord>(id);
    putFeature(rec);
  } catch (err) {
    toast(errorMessage(err), 'error');
  }
}

/** A comment belongs to a map feature or to a game-data record. */
export type CommentTarget = { field: 'feature' | 'dataRecord'; id: string };

export async function addComment(target: CommentTarget, body: string) {
  const s = getState();
  if (!s.user) return;
  const link = target.field === 'feature' ? { feature: target.id, map: s.mapId } : { dataRecord: target.id };
  if (target.field === 'feature' && !s.mapId) return;
  try {
    await pb.collection('comments').create({ ...link, author: s.user.id, body });
  } catch (err) {
    toast(errorMessage(err), 'error');
  }
}

export async function setCommentResolved(id: string, resolved: boolean) {
  try {
    await pb.collection('comments').update(id, { resolved });
  } catch (err) {
    toast(errorMessage(err), 'error');
  }
}

export async function deleteComment(id: string) {
  try {
    await pb.collection('comments').delete(id);
  } catch (err) {
    toast(errorMessage(err), 'error');
  }
}
